import React, { useRef, useEffect, useState } from 'react';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

type Image = {
  src: string;
  thumb: string;
  alt?: string;
  width?: number;
  height?: number;
};

type VideoProps = {
  src: string;
  poster?: string;
  width?: string;
  height?: string;
};

type GalleryProps = {
  images: Image[];
  title: string;
  imageSize?: {
    width: string;
    height: string;
  };
  bgImage?: number;
  video?: VideoProps;
};

const VideoPlayer: React.FC<VideoProps> = ({ 
  src, 
  poster, 
  width = '100%', 
  height = '400px' 
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInView, setIsInView] = useState(false);

  // Lazy loading avec Intersection Observer
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          // Charger la vidéo seulement quand elle est visible
          if (video.getAttribute('data-src')) {
            video.src = video.getAttribute('data-src') || '';
            video.load();
          }
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleLoadedData = () => {
    setIsLoading(false);
  };

  const handleError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.error('Erreur de chargement vidéo:', e);
    setIsLoading(false);
  };

  return (
    <div 
      className="video-container" 
      style={{ 
        position: 'relative',
        maxWidth: '800px',
        margin: '0 10px 40px 10px',
        borderRadius: '10px',
        overflow: 'hidden',
        zIndex: 2,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }}
    >
      {/* Loader pendant le chargement */}
      {isLoading && (
        <div 
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
            color: '#666',
            fontSize: '18px',
            fontWeight: '500'
          }}
        >
          Chargement de la vidéo...
        </div>
      )}
      
      <video
        ref={videoRef}
        data-src={src}
        poster={poster}
        controls
        playsInline
        autoPlay
        muted
        loop
        preload="metadata"
        style={{
          width,
          height,
          objectFit: 'cover',
          opacity: isLoading ? 0.5 : 1,
          transition: 'opacity 0.3s ease',
          display: 'block'
        }}
        onLoadedData={handleLoadedData}
        onError={handleError}
      >
        <source src={isInView ? src : ''} type="video/mp4" />
        Votre navigateur ne supporte pas la lecture vidéo.
      </video>
    </div>
  );
};

const Gallerie: React.FC<GalleryProps> = ({ 
  images = [], 
  title, 
  imageSize = { width: '300px', height: '300px' },
  bgImage = 4,
  video
}) => {
  const galleryRef = useRef<HTMLDivElement>(null);
  const lightboxRef = useRef<PhotoSwipeLightbox | null>(null);
  const [lightboxImages, setLightboxImages] = useState<Image[]>([]);

  // Chargement immédiat des thumbs, sans attendre les full images
  useEffect(() => {
    const preloadFullDimensions = async () => {
      const loaded = await Promise.all(
        images.map((image) =>
          new Promise<Image>((resolve) => {
            const img = new Image();
            img.onload = () => {
              resolve({
                ...image,
                width: img.naturalWidth,
                height: img.naturalHeight,
              });
            };
            img.onerror = () => {
              resolve({
                ...image,
                width: 1600,
                height: 1200,
              });
            };
            img.src = image.src;
          })
        )
      );
      setLightboxImages(loaded);
    };

    preloadFullDimensions();
  }, [images]);

  // Initialiser la lightbox quand les dimensions sont prêtes
  useEffect(() => {
    if (!galleryRef.current || lightboxImages.length === 0) return;

    if (lightboxRef.current) {
      lightboxRef.current.destroy();
    }

    lightboxRef.current = new PhotoSwipeLightbox({
      gallery: galleryRef.current,
      children: 'a',
      pswpModule: () => import('photoswipe'),
      bgOpacity: 0.9,
      spacing: 0.1,
      allowPanToNext: true,
      imageClickAction: 'zoom',
      tapAction: 'toggle-controls',
      doubleTapAction: 'zoom',
      preload: [1, 4],
      closeOnVerticalDrag: true,
    });

    lightboxRef.current.init();

    return () => {
      lightboxRef.current?.destroy();
      lightboxRef.current = null;
    };
  }, [lightboxImages]);

  return (
    <main className='gallerie'>
      <h2 className='gallery-title'>{title}</h2>

      {/* Affichage de la vidéo si fournie */}
      {video && (
        <div className="gallery-video-section">
          <VideoPlayer {...video} />
        </div>
      )}

      {images[bgImage] && (
        <div className='gallery-bg-image' transition-style="in:wipe:top">
          <img src={images[bgImage].src} alt={images[bgImage].alt} />
        </div>
      )}

      <div
        ref={galleryRef}
        className="photoswipe-gallery"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          justifyContent: 'center',
          padding: '20px'
        }}
      >
        {images.map((image, index) => {
          const full = lightboxImages.find((img) => img.src === image.src);
          return (
            <a
              key={index}
              href={image.src}
              data-pswp-width={full?.width ?? 1600}
              data-pswp-height={full?.height ?? 1200}
              style={{ textDecoration: 'none' }}
              transition-style="in:wipe:bottom"
            >
              <img
                src={image.thumb}
                alt={image.alt || `Image ${index + 1}`}
                style={{
                  width: imageSize.width,
                  height: imageSize.height,
                  objectFit: 'cover',
                  cursor: 'zoom-in',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.005)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              />
            </a>
          );
        })}
      </div>
    </main>
  );
};

export default Gallerie;